import { resolveDesignViewport } from "./DesignSpace";
import { resolveScreenShape, type ScreenShape, type ScreenShapeHint } from "./ScreenShape";

/** One fact about the screen: its CSS size, its design-unit size, the scale between them, its shape. */
export interface ViewportSnapshot {
  css: { width: number; height: number };
  /** 1920 tall; the width follows the aspect ratio. */
  design: { width: number; height: number };
  /** CSS pixels per design unit. */
  scale: number;
  shape: ScreenShape;
}

type Listener = (snapshot: ViewportSnapshot) => void;

/**
 * The viewport store every layout consumer subscribes to — the design-space root, the DOM chrome
 * root, the responsive views. Fed the CSS size by the app's resize observer and the device hint
 * once boot has it (the shape re-resolves; the hint wins over the width breakpoint).
 */
export class ViewportStore {
  private snapshot: ViewportSnapshot;
  private hint: ScreenShapeHint = null;
  private readonly listeners = new Set<Listener>();

  public constructor(initialSize?: { width: number; height: number }) {
    this.snapshot = this.compute(Math.max(1, initialSize?.width ?? 1), Math.max(1, initialSize?.height ?? 1));
  }

  public getSnapshot(): ViewportSnapshot {
    return this.snapshot;
  }

  /** Calls the listener with the current snapshot at once, then on every change. */
  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public setViewportSize(width: number, height: number): void {
    this.update(Math.max(1, Math.round(width)), Math.max(1, Math.round(height)));
  }

  /** The device the launch context names, once boot has it (the shape re-resolves; the hint wins over the width breakpoint). */
  public setShapeHint(hint: ScreenShapeHint): void {
    this.hint = hint;
    this.update(this.snapshot.css.width, this.snapshot.css.height);
  }

  private update(width: number, height: number): void {
    const next = this.compute(width, height);
    const prev = this.snapshot;
    if (prev.css.width === next.css.width && prev.css.height === next.css.height && prev.shape === next.shape) return;
    this.snapshot = next;
    for (const listener of [...this.listeners]) listener(next);
  }

  private compute(width: number, height: number): ViewportSnapshot {
    const design = resolveDesignViewport({ width, height });
    return {
      css: { width, height },
      design: { width: design.width, height: design.height },
      scale: design.scale,
      shape: resolveScreenShape({ width, height, hint: this.hint }),
    };
  }
}
